import carePlan from '@/pages/participantDocuments/legacy/forms/careplan.json';
import initialAssessment from '@/pages/participantDocuments/legacy/forms/oldIA.json';
import type { ProfileEntity, ProfileField } from './types';

export interface ProfileFormField {
  label: string;
  section: string;
  multiline: boolean;
  options: { value: string; label: string }[];
}
const label = (value: unknown): string => typeof value === 'string' ? value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';

/** Read maintained form definitions for presentation; server metadata owns the available fields. */
const formFields = (template: unknown): Map<string, ProfileFormField> => {
  const fields = new Map<string, ProfileFormField>();
  const visit = (items: unknown, heading = '') => {
    if (!Array.isArray(items)) return;
    let section = heading;
    for (const value of items) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
      const item = value as Record<string, unknown>;
      const text = label(item.label) || label(item.text);
      if (item.type === 'heading') { section = text; continue; }
      if (item.type === 'section' || item.type === 'collapsible') {
        visit(item.children ?? item.fields, text || section);
        continue;
      }
      const name = typeof item.key === 'string' ? item.key : typeof item.name === 'string' ? item.name : '';
      if (!name || fields.has(name)) continue;
      const options: ProfileFormField['options'] = [];
      for (const value of Array.isArray(item.options) ? item.options : []) {
        if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
        const option = value as Record<string, unknown>;
        if (typeof option.value !== 'string' && typeof option.value !== 'number') continue;
        if (!String(option.value) || options.some(item => item.value === String(option.value))) continue;
        options.push({ value: String(option.value), label: label(option.label) || label(option.text) || String(option.value) });
      }
      fields.set(name, { label: text, section, multiline: ['textarea', 'textAreaInput'].includes(String(item.type)), options });
    }
  };
  visit(template);
  return fields;
};
const definitions: Readonly<Record<string, ReadonlyMap<string, ProfileFormField>>> = {
  careplan: formFields(carePlan), initialassessment: formFields(initialAssessment),
  location: new Map(Object.entries({
    location: ['Location', 'Location', false], locationdetail: ['Location details', 'Location', true],
    contact: ['Contact', 'Location', true], dischargeplan: ['Discharge/treatment Plan', 'Location', true],
    pharmacynotes: ['Pharmacy notes', 'Location', true],
    ih_request_status: ['IH Request Status', 'Interim housing', false], ih_request_date: ['IH request date', 'Interim housing', false],
    restrictions: ['Restrictions', 'Interim housing', true], comments: ['Comments', 'Interim housing', true],
    ih_assignment_date: ['Date of IH Assignment', 'Interim housing', false], notes: ['Notes', 'Interim housing', true],
    high_priority_status: ['High Priority', 'Interim housing', false],
    psh_search_status: ['PSH Search Status', 'Permanent supportive housing', false],
    psh_search_status_date: ['PSH search status date', 'Permanent supportive housing', false],
    psh_search_notes: ['PSH Search Notes', 'Permanent supportive housing', true],
    psh_type: ['PSH Type', 'Permanent supportive housing', false],
    psh_funding_source: ['PSH Funding Source', 'Permanent supportive housing', false],
  } satisfies Record<string, [string, string, boolean]>).map(([name, [label, section, multiline]]) => [name, { label, section, multiline,
    options: name === 'psh_search_status' ? [{ value: '0', label: 'Status Not Set' }, { value: '1', label: 'Not approved' }, { value: '2', label: 'Approved' }] : [],
  }])),
};
export const profileFormField = (entity: string, field: string): ProfileFormField | undefined => definitions[entity]?.get(field);
export const profileFormGroups = (entity: ProfileEntity, editableOnly = true): { label: string; fields: ProfileField[] }[] => {
  const groups = new Map<string, ProfileField[]>();
  for (const field of entity.fields) {
    if (editableOnly && field.editable === false) continue;
    const section = definitions[entity.name]?.get(field.name)?.section ?? '';
    if (!groups.has(section)) groups.set(section, []);
    groups.get(section)!.push(field);
  }
  return Array.from(groups).map(([label, fields]) => ({ label, fields }));
};
